import "./DocumentTypePalette.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { useMetadocOptions } from "../../hooks/page-operations/useMetadocOptions";
import { getDocumentTypeLabel } from "../../utils/documentTypeUtils";
import { formatSelectionText } from "../thumbnails/utils/thumbnailUtils";

import type { MetadocOption } from "services/metadoc-api";

const SHIFTED_NUMBER_SYMBOLS: Record<string, string> = {
  "1": "!",
  "2": "@",
  "3": "#",
  "4": "$",
  "5": "%",
  "6": "^",
  "7": "&",
  "8": "*",
  "9": "(",
  "0": ")",
};

const getShortcutFromKeyboardEvent = (event: KeyboardEvent): string | null => {
  if (event.code.startsWith("Key")) {
    return event.code.slice(3).toLowerCase();
  }

  if (event.code.startsWith("Digit")) {
    return event.code.slice(5).toLowerCase();
  }

  if (event.code.startsWith("Numpad")) {
    const numpadValue = event.code.slice(6);
    if (/^[0-9]$/.test(numpadValue)) {
      return numpadValue;
    }
  }

  const lowerKey = event.key.toLowerCase();
  return lowerKey.length === 1 ? lowerKey : null;
};

type PageRangeValidationError = string | null;

const validatePageRange = (
  startPage: number,
  endPage: number,
  minPage: number,
  maxPage: number,
): PageRangeValidationError => {
  if (Number.isNaN(startPage) || Number.isNaN(endPage)) {
    return "Enter valid page numbers.";
  }

  if (startPage < minPage || endPage < minPage) {
    return `Page numbers must be at least ${minPage}.`;
  }

  if (startPage > maxPage || endPage > maxPage) {
    return `Page numbers must be between ${minPage} and ${maxPage}.`;
  }

  if (startPage > endPage) {
    return "Start page must be before end page.";
  }

  return null;
};

type DocumentTypePaletteProps = {
  onSelectType: (type: string) => Promise<boolean>;
  onSelectTypeToRange?: (
    type: string,
    startPage: number,
    endPage: number,
  ) => Promise<boolean>;
  disabled?: boolean;
  selectedPages?: Set<number>;
  currentPage: number;
  totalPages: number;
};

const DocumentTypePalette = ({
  onSelectType,
  onSelectTypeToRange,
  disabled = false,
  selectedPages,
  currentPage,
  totalPages,
}: DocumentTypePaletteProps) => {
  const { options, getDocumentTypeColor, getDocumentTypeShortcut } =
    useMetadocOptions();
  const hasSelection = selectedPages && selectedPages.size > 0;
  const isTypeAssignmentDisabled = disabled || !hasSelection;
  const isBulkAssignmentDisabled =
    disabled || !onSelectTypeToRange || totalPages < 1;
  const [isLoading, setIsLoading] = useState(false);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isRangeModalOpen, setIsRangeModalOpen] = useState(false);
  const [rangeType, setRangeType] = useState<string | null>(null);
  const [startPageInput, setStartPageInput] = useState("");
  const [endPageInput, setEndPageInput] = useState("");
  const [rangeError, setRangeError] = useState("");
  const endPageInputRef = useRef<HTMLInputElement>(null);

  const rangeMinPage = 1;
  const rangeMaxPage = totalPages;

  const getSelectionText = () => {
    if (!selectedPages || selectedPages.size === 0)
      return "Select pages to assign type";

    const selectionText = formatSelectionText(selectedPages);
    // convert "Page 5 selected" to "Assign type to page 5"
    // convert "Pages 3-8 selected" to "Assign type to pages 3-8"
    return selectionText
      .replace("selected", "")
      .replace(/^Page/, "Assign type to page")
      .replace(/^Pages/, "Assign type to pages");
  };

  const handleSelectType = useCallback(
    async (type: string) => {
      if (isTypeAssignmentDisabled || isLoading) return;

      setActiveType(type);
      setIsLoading(true);

      try {
        await onSelectType(type);
      } finally {
        setIsLoading(false);
        setActiveType(null);
      }
    },
    [isTypeAssignmentDisabled, isLoading, onSelectType],
  );

  const getModalDefaults = useCallback(() => {
    if (selectedPages && selectedPages.size > 0) {
      const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
      return {
        startPage: sortedPages[0],
        endPage: sortedPages[sortedPages.length - 1],
      };
    }

    const fallbackPage = Math.min(Math.max(currentPage, 1), totalPages || 1);
    return {
      startPage: fallbackPage,
      endPage: fallbackPage,
    };
  }, [currentPage, selectedPages, totalPages]);

  const openRangeModal = useCallback(
    (type: string) => {
      if (isBulkAssignmentDisabled || isLoading) {
        return;
      }

      const { startPage, endPage } = getModalDefaults();
      setRangeType(type);
      setStartPageInput(String(startPage));
      setEndPageInput(String(endPage));
      setRangeError("");
      setIsRangeModalOpen(true);
    },
    [getModalDefaults, isBulkAssignmentDisabled, isLoading],
  );

  const closeRangeModal = useCallback(() => {
    if (isLoading) {
      return;
    }

    setIsRangeModalOpen(false);
    setRangeType(null);
    setStartPageInput("");
    setEndPageInput("");
    setRangeError("");
  }, [isLoading]);

  const clampPageInputToRange = useCallback(
    (value: string) => {
      if (!value) {
        return "";
      }

      const parsedValue = Number.parseInt(value, 10);
      if (Number.isNaN(parsedValue)) {
        return value;
      }

      if (parsedValue > rangeMaxPage) {
        return String(rangeMaxPage);
      }

      if (parsedValue < rangeMinPage) {
        return String(rangeMinPage);
      }

      return value;
    },
    [rangeMaxPage, rangeMinPage],
  );

  const handleRangeSubmit = useCallback(async () => {
    if (!rangeType || isLoading || !onSelectTypeToRange) {
      return;
    }

    const startPage = Number.parseInt(startPageInput, 10);
    const endPage = Number.parseInt(endPageInput, 10);
    const validationError = validatePageRange(
      startPage,
      endPage,
      rangeMinPage,
      rangeMaxPage,
    );
    if (validationError) {
      setRangeError(validationError);
      return;
    }

    setRangeError("");
    setActiveType(rangeType);
    setIsLoading(true);
    try {
      const isAssigned = await onSelectTypeToRange(
        rangeType,
        startPage,
        endPage,
      );
      if (isAssigned) {
        closeRangeModal();
      }
    } finally {
      setIsLoading(false);
      setActiveType(null);
    }
  }, [
    closeRangeModal,
    endPageInput,
    isLoading,
    onSelectTypeToRange,
    rangeMaxPage,
    rangeMinPage,
    rangeType,
    startPageInput,
  ]);

  useEffect(() => {
    if (!isRangeModalOpen) {
      return;
    }

    const focusTimeoutId = window.setTimeout(() => {
      if (endPageInputRef.current) {
        endPageInputRef.current.focus();
        endPageInputRef.current.select();
      }
    }, 0);

    return () => window.clearTimeout(focusTimeoutId);
  }, [isRangeModalOpen]);

  const shortcutKeys = useMemo(
    () =>
      options
        .map((opt) => opt.shortcut)
        .filter((shortcut): shortcut is string => Boolean(shortcut))
        .join(","),
    [options],
  );
  const shiftedShortcutKeys = useMemo(
    () =>
      options
        .filter((opt): opt is MetadocOption & { shortcut: string } =>
          Boolean(opt.shortcut),
        )
        .flatMap((opt) => {
          const shortcut = opt.shortcut.toLowerCase();
          const shiftedSymbol = SHIFTED_NUMBER_SYMBOLS[shortcut];
          return shiftedSymbol
            ? [`shift+${shortcut}`, `shift+${shiftedSymbol}`]
            : [`shift+${shortcut}`];
        })
        .join(","),
    [options],
  );

  useHotkeys(
    shortcutKeys,
    (e) => {
      if (
        e.shiftKey ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        isRangeModalOpen
      ) {
        return;
      }
      const pressedKey = getShortcutFromKeyboardEvent(e);
      if (!pressedKey) {
        return;
      }
      const matchedOption = options.find(
        (opt) => opt.shortcut?.toLowerCase() === pressedKey,
      );
      if (matchedOption) {
        e.preventDefault();
        void handleSelectType(matchedOption.document_type);
      }
    },
    {
      enabled: !isRangeModalOpen,
      enableOnFormTags: false,
    },
    [handleSelectType, isRangeModalOpen, options],
  );

  useHotkeys(
    shiftedShortcutKeys,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isRangeModalOpen) {
        return;
      }

      const pressedKey = getShortcutFromKeyboardEvent(e);
      if (!pressedKey) {
        return;
      }
      const matchedOption = options.find(
        (opt) => opt.shortcut?.toLowerCase() === pressedKey,
      );

      if (matchedOption) {
        e.preventDefault();
        openRangeModal(matchedOption.document_type);
      }
    },
    {
      enabled: !isRangeModalOpen,
      enableOnFormTags: false,
    },
    [isRangeModalOpen, openRangeModal, options],
  );

  useHotkeys(
    "enter",
    (e) => {
      if (!isRangeModalOpen || isLoading) {
        return;
      }

      e.preventDefault();
      void handleRangeSubmit();
    },
    {
      enabled: isRangeModalOpen,
      enableOnFormTags: true,
      preventDefault: true,
    },
    [handleRangeSubmit, isLoading, isRangeModalOpen],
  );

  useHotkeys(
    "escape",
    (e) => {
      if (!isRangeModalOpen || isLoading) {
        return;
      }

      e.preventDefault();
      closeRangeModal();
    },
    {
      enabled: isRangeModalOpen,
      enableOnFormTags: true,
      preventDefault: true,
    },
    [closeRangeModal, isLoading, isRangeModalOpen],
  );

  return (
    <>
      <div
        className={`document-type-palette ${disabled ? "document-type-palette--disabled" : ""} ${isCollapsed ? "document-type-palette--collapsed" : ""}`}
      >
        <button
          className="document-type-palette__header"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={
            isCollapsed ? "Expand Document Types" : "Collapse Document Types"
          }
        >
          <div className="document-type-palette__header-content">
            <span className="material-icons">category</span>
            <h4 className="document-type-palette__title">Document Types</h4>
          </div>
          <span className="material-icons document-type-palette__caret">
            {isCollapsed ? "expand_less" : "expand_more"}
          </span>
        </button>

        {!isCollapsed && (
          <>
            {hasSelection && !isTypeAssignmentDisabled && (
              <div className="document-type-palette__selection-info">
                {getSelectionText()}
              </div>
            )}

            {!disabled && (
              <div className="document-type-palette__bulk-hint">
                Press <kbd>Shift</kbd> + type shortcut for page-range assignment
              </div>
            )}

            <div className="document-type-palette__grid">
              {options.map((option) => {
                const color = getDocumentTypeColor(option.document_type);
                const shortcut = getDocumentTypeShortcut(option.document_type);
                const displayLabel = getDocumentTypeLabel(option.document_type);
                const isActive = activeType === option.document_type;
                const isChipDisabled = isTypeAssignmentDisabled || isLoading;

                return (
                  <button
                    key={option.document_type}
                    className={`document-type-palette__chip ${isActive ? "document-type-palette__chip--active" : ""}`}
                    onClick={() => void handleSelectType(option.document_type)}
                    disabled={isChipDisabled}
                    title={`${option.document_type}${shortcut ? ` (Press ${shortcut.toUpperCase()})` : ""}`}
                    style={{
                      backgroundColor: isChipDisabled ? `${color}30` : color,
                      borderColor: isChipDisabled ? color : "transparent",
                    }}
                  >
                    {isActive && isLoading ? (
                      <span className="document-type-palette__chip-spinner" />
                    ) : (
                      <span className="document-type-palette__chip-label">
                        {displayLabel}
                      </span>
                    )}
                    {shortcut && !isActive && (
                      <span className="document-type-palette__chip-shortcut">
                        {shortcut.toUpperCase()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <KaboodleModal
        isOpen={isRangeModalOpen}
        onClose={closeRangeModal}
        footer={
          <div className="flex jc-c document-type-palette__range-modal-footer">
            <Button onClick={closeRangeModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              className="primary"
              onClick={() => void handleRangeSubmit()}
              disabled={
                isLoading || !rangeType || !startPageInput || !endPageInput
              }
            >
              {isLoading ? "Assigning..." : "Assign Type"}
            </Button>
          </div>
        }
      >
        <Card heading="Assign Document Type by Range" type="full-width">
          <div className="document-type-palette__range-modal-body">
            <div className="document-type-palette__range-modal-type">
              <span className="document-type-palette__range-modal-label">
                Document Type
              </span>
              <strong>
                {rangeType ? getDocumentTypeLabel(rangeType) : "--"}
              </strong>
            </div>
            <div className="document-type-palette__range-modal-fields">
              <label className="document-type-palette__range-modal-field">
                <span className="document-type-palette__range-modal-label">
                  Start page
                </span>
                <input
                  type="number"
                  min={rangeMinPage}
                  max={rangeMaxPage}
                  value={startPageInput}
                  onChange={(e) => {
                    setStartPageInput(clampPageInputToRange(e.target.value));
                    if (rangeError) {
                      setRangeError("");
                    }
                  }}
                  disabled={isLoading}
                />
              </label>
              <label className="document-type-palette__range-modal-field">
                <span className="document-type-palette__range-modal-label">
                  End page
                </span>
                <input
                  ref={endPageInputRef}
                  type="number"
                  min={rangeMinPage}
                  max={rangeMaxPage}
                  value={endPageInput}
                  onChange={(e) => {
                    setEndPageInput(clampPageInputToRange(e.target.value));
                    if (rangeError) {
                      setRangeError("");
                    }
                  }}
                  disabled={isLoading}
                />
              </label>
            </div>
            <div className="document-type-palette__range-modal-total-pages">
              Total pages: {totalPages}
            </div>
            {rangeError && (
              <div className="document-type-palette__range-modal-error">
                {rangeError}
              </div>
            )}
          </div>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default DocumentTypePalette;
